/**
 * ScoreDetailModal.jsx — 월간 점수 항목별 내역 (읽기 전용)
 *
 * 요약 화면에는 "잡은 미팅 수"가 없어 계약율 분모가 보이지 않는다.
 * 이 모달이 실적값 → 점수 변환 과정을 그대로 보여준다.
 * 수정은 관리자 "점수 관리"에서만 한다.
 */
import React from 'react';
import Modal from '../../../components/Modal';

const fmtNum = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString('ko-KR') : '-');
const fmtScore = (n) => (Number.isFinite(Number(n)) ? String(Math.round(Number(n) * 10) / 10) : '-');

const th = { padding: '7px 8px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textAlign: 'left', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
const td = { padding: '9px 8px', fontSize: 13, color: 'var(--text-sub)', borderBottom: '1px solid var(--border)' };

function rawText(item, data) {
  if (item.mode === 'manual') return `${fmtScore(item.rawValue)}점`;
  if (item.rawValue === null || item.rawValue === undefined) return '-';
  if (item.key === 'contractRate') {
    return `${fmtScore(item.rawValue)}% (신규 ${fmtNum(data.derived?.contractRateNumerator)}건 ÷ 미팅 ${fmtNum(data.derived?.contractRateDenominator)}개)`;
  }
  return `${fmtNum(item.rawValue)}${item.unit || ''}`;
}

export default function ScoreDetailModal({ open, onClose, data, year, month, isMobile }) {
  if (!data) return null;
  return (
    <Modal
      open={open}
      title={`${year}년 ${month}월 점수`}
      icon="📊"
      onClose={onClose}
      footer={<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', cursor: 'pointer', fontWeight: 700 }}>닫기</button>
      </div>}
      width="min(560px, 96vw)"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)' }}>{data.name}</span>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: 'rgba(124,58,237,0.12)', color: '#7c3aed' }}>
          {data.employmentLabel}
        </span>
        {Number.isFinite(Number(data.weeklyHours)) && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>주 소정근로 {data.weeklyHours}시간</span>
        )}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? 340 : 460 }}>
          <thead>
            <tr>
              <th style={th}>항목</th>
              <th style={th}>내 실적</th>
              <th style={{ ...th, textAlign: 'right' }}>점수</th>
            </tr>
          </thead>
          <tbody>
            {(data.items || []).map((item) => (
              <tr key={item.key}>
                <td style={{ ...td, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap' }}>
                  {item.label}
                </td>
                <td style={td}>
                  {rawText(item, data)}
                  {item.note && <span style={{ marginLeft: 6, fontSize: 11, color: '#dc2626' }}>{item.note}</span>}
                  {item.memo && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>사유: {item.memo}</div>}
                </td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: 'var(--text)', whiteSpace: 'nowrap' }}>
                  {fmtScore(item.score)}점
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ ...td, borderBottom: 'none', fontWeight: 800, color: 'var(--text)' }}>합계</td>
              <td style={{ ...td, borderBottom: 'none' }} />
              <td style={{ ...td, borderBottom: 'none', textAlign: 'right', fontWeight: 900, fontSize: 16, color: '#7c3aed' }}>
                {fmtScore(data.totalScore)}점
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {(data.warnings || []).length > 0 && (
        <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: 'rgba(220,38,38,0.08)', color: '#dc2626', fontSize: 12 }}>
          {data.warnings.map((w) => <div key={w}>· {w}</div>)}
        </div>
      )}

      <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.7 }}>
        · 계약장수는 요약 화면의 <b>총 장수</b>와 같은 값입니다(신규+재주문, 환산장수 반영).<br />
        · 잡은 미팅은 이 달에 <b>전화해서 미팅을 잡은</b> 건수입니다.
      </div>

      {/* 이 달에 적용된 점수 기준 — 어떻게 하면 점수가 오르는지 직원이 직접 확인 */}
      {data.criteria && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text)', marginBottom: 8 }}>
            {year}년 {month}월 점수 기준
            <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: 'rgba(124,58,237,0.12)', color: '#7c3aed' }}>
              {data.employmentLabel}
            </span>
          </div>

          {data.criteria.items.map((c) => (
            <div key={c.key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>
              <span style={{ flex: '0 0 76px', fontSize: 12, fontWeight: 700, color: 'var(--text-sub)' }}>{c.label}</span>
              <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                {c.mode === 'manual' ? (
                  <>최대 {c.maxPoints}점{c.description ? ` (${c.description})` : ''}</>
                ) : (
                  <>
                    {c.bands.map(([x, y], i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <span key={`${c.key}-${i}`} style={{ whiteSpace: 'nowrap' }}>
                        {i > 0 && <span style={{ margin: '0 5px', opacity: 0.5 }}>·</span>}
                        {fmtNum(x)}{c.unit} <b style={{ color: 'var(--text-sub)' }}>{y}점</b>
                      </span>
                    ))}
                    {c.overflow && (
                      <div style={{ color: '#7c3aed' }}>
                        {fmtNum((c.basePoint ? c.basePoint.value : c.bands[c.bands.length - 1][0]))}{c.unit} 초과 시 {fmtNum(c.overflow.unit)}{c.unit}당 +{c.overflow.points}점
                      </div>
                    )}
                    {c.maxPoints !== null && c.maxPoints !== undefined && (
                      <span style={{ marginLeft: 6, fontSize: 11 }}>(최대 {c.maxPoints}점)</span>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}

          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
            구간 사이 값은 비례로 계산됩니다. (예: 두 구간의 정확히 중간이면 점수도 중간)
          </div>
        </div>
      )}
    </Modal>
  );
}
